import React, { useState, useEffect } from 'react';
import styles from './PaymentMethodModal.module.css';

const PaymentMethodModal = ({ isOpen, onClose, onSave, editingMethod = null }) => {
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    isDefault: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or editing method changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        cardNumber: editingMethod?.last_four ? '•••• •••• •••• ' + editingMethod.last_four : '',
        cardHolder: editingMethod?.card_holder_name || '',
        expiryMonth: editingMethod?.expiry_month || '',
        expiryYear: editingMethod?.expiry_year || '',
        isDefault: editingMethod?.is_default || false
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, editingMethod]);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!editingMethod && !formData.cardNumber.replace(/\s/g, '').match(/^\d{16}$/)) {
      newErrors.cardNumber = 'Please enter a valid 16-digit card number';
    }

    if (!formData.cardHolder.trim()) {
      newErrors.cardHolder = 'Card holder name is required';
    }

    if (!formData.expiryMonth || !formData.expiryYear) {
      newErrors.expiry = 'Please select expiry date';
    } else {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100;
      const currentMonth = currentDate.getMonth() + 1;
      const expYear = parseInt(formData.expiryYear);
      const expMonth = parseInt(formData.expiryMonth);

      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        newErrors.expiry = 'Card has expired';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const error = await onSave({
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
        cardHolder: formData.cardHolder,
        expiryMonth: formData.expiryMonth,
        expiryYear: formData.expiryYear,
        isDefault: formData.isDefault
      });

      // If onSave returns an error, display it
      if (error) {
        setErrors({ 
          submit: error.response?.data?.message || 'Failed to save payment method' 
        });
        setIsSubmitting(false);
        return;
      }

      // Reset form only if save was successful
      setFormData({
        cardNumber: '',
        cardHolder: '',
        expiryMonth: '',
        expiryYear: '',
        isDefault: false
      });
    } catch (error) {
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to save payment method' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData({ ...formData, cardNumber: formatted });
  };

  const handleCancel = () => {
    setIsSubmitting(false);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleCancel()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>{editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}</h2>
          <button className={styles.closeButton} onClick={handleCancel}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Card Number</label>
            <div className={styles.cardInputWrapper}>
              <i className={`fab fa-cc-${getCardType(formData.cardNumber)}`}></i>
              <input
                type="text"
                placeholder="1234 5678 9012 3456"
                value={formData.cardNumber}
                onChange={handleCardNumberChange}
                maxLength="19"
                className={errors.cardNumber ? styles.error : ''}
                disabled={!!editingMethod}
              />
            </div>
            {errors.cardNumber && <span className={styles.errorText}>{errors.cardNumber}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Card Holder Name</label>
            <input
              type="text"
              placeholder="John Doe"
              value={formData.cardHolder}
              onChange={(e) => setFormData({ ...formData, cardHolder: e.target.value })}
              className={errors.cardHolder ? styles.error : ''}
            />
            {errors.cardHolder && <span className={styles.errorText}>{errors.cardHolder}</span>}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Expiry Date</label>
              <div className={styles.expiryInputs}>
                <select
                  value={formData.expiryMonth}
                  onChange={(e) => setFormData({ ...formData, expiryMonth: e.target.value })}
                  className={errors.expiry ? styles.error : ''}
                >
                  <option value="">MM</option>
                  {Array.from({ length: 12 }, (_, i) => {
                    const month = (i + 1).toString().padStart(2, '0');
                    return <option key={month} value={month}>{month}</option>;
                  })}
                </select>
                <select
                  value={formData.expiryYear}
                  onChange={(e) => setFormData({ ...formData, expiryYear: e.target.value })}
                  className={errors.expiry ? styles.error : ''}
                >
                  <option value="">YY</option>
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = (new Date().getFullYear() + i).toString().slice(-2);
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
              </div>
              {errors.expiry && <span className={styles.errorText}>{errors.expiry}</span>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
              Set as default payment method
            </label>
          </div>

          {errors.submit && (
            <div className={styles.submitError}>
              {errors.submit}
            </div>
          )}

          <div className={styles.formActions}>
            <button 
              type="button" 
              className={styles.cancelButton} 
              onClick={handleCancel} 
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className={styles.saveButton} disabled={isSubmitting}>
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                editingMethod ? 'Update Card' : 'Add Card'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function to determine card type based on number
const getCardType = (number) => {
  const cleaned = number.replace(/\D/g, '');
  if (cleaned.startsWith('4')) return 'visa';
  if (cleaned.match(/^5[1-5]/)) return 'mastercard';
  if (cleaned.match(/^3[47]/)) return 'amex';
  if (cleaned.match(/^6/)) return 'discover';
  return 'credit-card';
};

export default PaymentMethodModal; 